import React, { useState, useCallback } from 'react';
import {
  Box,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  IconButton,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { useAppTheme } from '../../app/providers/ThemeProvider';

export interface SecondaryNavItem {
  id: string;
  label: string;
  icon?: React.ReactElement;
  disabled?: boolean;
  requiresAuth?: boolean;
  requiresRole?: string;
  badge?: number;
}

interface SecondaryNavProps {
  items: SecondaryNavItem[];
  value: string;
  onChange: (value: string) => void;
  sticky?: boolean;
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  ariaLabel?: string;
}

const SecondaryNav = React.memo<SecondaryNavProps>(
  ({
    items,
    value,
    onChange,
    sticky = true,
    isAuthenticated = false,
    isAdmin = false,
    ariaLabel = 'Secondary navigation',
  }) => {
    const muiTheme = useTheme();
    const { customTokens: tokens } = useAppTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(anchorEl);

    // Filter items based on auth and admin requirements
    const visibleItems = items.filter(item => {
      if (item.requiresAuth && !isAuthenticated) {
        return false;
      }
      // For items that require a role, check if user is admin or has the specific role
      // Note: requiresRole is now legacy - items should check permissions in parent
      if (item.requiresRole && !isAdmin) {
        return false;
      }
      return true;
    });

    const handleTabChange = useCallback(
      (_event: React.SyntheticEvent, newValue: string) => {
        onChange(newValue);
      },
      [onChange],
    );

    const handleMenuClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    }, []);

    const handleMenuClose = useCallback(() => {
      setAnchorEl(null);
    }, []);

    const handleMenuItemClick = useCallback(
      (itemId: string) => {
        onChange(itemId);
        handleMenuClose();
      },
      [onChange, handleMenuClose],
    );

    // On mobile, show first 2-3 items as tabs and rest in overflow menu
    const maxVisibleTabs = isMobile ? 2 : 4;
    const visibleTabs = visibleItems.slice(0, maxVisibleTabs);
    const overflowItems = visibleItems.slice(maxVisibleTabs);
    const hasOverflow = overflowItems.length > 0;

    if (visibleItems.length === 0) {
      return null;
    }

    return (
      <Box
        sx={{
          borderBottom: `1px solid ${muiTheme.palette.divider}`,
          backgroundColor: muiTheme.palette.background.paper,
          ...(sticky && {
            position: 'sticky',
            top: 0,
            zIndex: muiTheme.zIndex.appBar - 1,
            backdropFilter: 'blur(8px)',
            backgroundColor:
              muiTheme.palette.mode === 'light'
                ? 'rgba(255, 255, 255, 0.9)'
                : 'rgba(18, 18, 18, 0.9)',
          }),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={value}
            onChange={handleTabChange}
            variant={isMobile ? 'standard' : 'standard'}
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label={ariaLabel}
            sx={{
              minHeight: 48,
              '& .MuiTabs-flexContainer': {
                gap: isMobile ? 0 : tokens.spacing[1],
              },
              '& .MuiTab-root': {
                minHeight: 48,
                textTransform: 'none',
                fontWeight: tokens.typography.fontWeight.medium,
                fontSize: tokens.typography.fontSize.sm,
                color: 'text.secondary',
                transition: `all ${tokens.motion.duration.normal} ${tokens.motion.easing.ease}`,
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'action.hover',
                },
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: tokens.typography.fontWeight.semibold,
                },
                '& .MuiTab-iconWrapper': {
                  marginBottom: '4px',
                  '& svg': {
                    fontSize: '1.1rem',
                  },
                },
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '2px',
              },
            }}
          >
            {visibleTabs.map(item => {
              const isSelected = value === item.id;

              return (
                <Tab
                  key={item.id}
                  value={item.id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="inherit"
                        sx={{
                          fontSize: {
                            xs: tokens.typography.fontSize.xs,
                            sm: tokens.typography.fontSize.sm,
                          },
                        }}
                      >
                        {item.label}
                      </Typography>
                      {item.badge && item.badge > 0 && (
                        <Box
                          sx={{
                            backgroundColor: isSelected
                              ? 'primary.main'
                              : 'action.selected',
                            color: isSelected ? 'primary.contrastText' : 'text.secondary',
                            borderRadius: '50%',
                            minWidth: 18,
                            height: 18,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: tokens.typography.fontWeight.bold,
                            ml: 0.5,
                          }}
                        >
                          {item.badge > 99 ? '99+' : item.badge}
                        </Box>
                      )}
                    </Box>
                  }
                  icon={item.icon}
                  iconPosition="start"
                  disabled={item.disabled}
                  sx={{
                    minWidth: isMobile ? 'auto' : 120,
                    px: { xs: tokens.spacing[2], sm: tokens.spacing[3] },
                  }}
                />
              );
            })}
          </Tabs>

          {hasOverflow && (
            <>
              <IconButton
                onClick={handleMenuClick}
                size="small"
                sx={{
                  ml: tokens.spacing[1],
                  mr: tokens.spacing[2],
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
                aria-label="More navigation options"
                aria-controls={isMenuOpen ? 'overflow-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={isMenuOpen ? 'true' : undefined}
              >
                <MoreVertIcon />
              </IconButton>

              <Menu
                id="overflow-menu"
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 160,
                  },
                }}
              >
                {overflowItems.map(item => (
                  <MenuItem
                    key={item.id}
                    onClick={() => handleMenuItemClick(item.id)}
                    selected={value === item.id}
                    disabled={item.disabled}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontSize: tokens.typography.fontSize.sm,
                    }}
                  >
                    {item.icon}
                    <Typography variant="inherit">{item.label}</Typography>
                    {item.badge && item.badge > 0 && (
                      <Box
                        sx={{
                          backgroundColor: 'action.selected',
                          color: 'text.secondary',
                          borderRadius: '50%',
                          minWidth: 16,
                          height: 16,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.7rem',
                          fontWeight: tokens.typography.fontWeight.bold,
                          ml: 'auto',
                        }}
                      >
                        {item.badge > 99 ? '99+' : item.badge}
                      </Box>
                    )}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>
      </Box>
    );
  },
);

SecondaryNav.displayName = 'SecondaryNav';

export default SecondaryNav;
